import { useCallback } from "react";
import { useApp } from "@/contexts/AppContext";
import type { ComprehensiveScenario } from "@/types/scenarios";

/**
 * Predicate for the "Your Scenarios" gallery: a scenario belongs to the current
 * user only when they own it or it has been explicitly shared with them.
 *
 * This is deliberately narrower than {@link useVisibleScenarios} — inherited,
 * status-conditional access (view_all_active_scenarios / view_parked_scenarios)
 * and workflow stage assignment do NOT surface a scenario in the gallery, even
 * though the user can still see it elsewhere (comparison, parked dashboard,
 * pending approvals).
 *
 * - Live mode: uses the authoritative flags from vw_Scenarios — `isOwner`
 *   (via `currentUserPermission === 'owner'`) or `isShared`.
 * - Demo mode: the owner is the creator, and explicit shares live on
 *   `scenario.shares`.
 */
export function useMyScenarios(): {
	isMyScenario: (scenario: ComprehensiveScenario) => boolean;
} {
	const { currentUser, mode } = useApp();
	const userId = currentUser.id;

	const isMyScenario = useCallback(
		(scenario: ComprehensiveScenario): boolean => {
			if (mode === "live") {
				return (
					scenario.currentUserPermission === "owner" ||
					scenario.isShared === true
				);
			}
			// Demo mode — no server flags, derive from the in-memory scenario.
			return (
				scenario.createdBy === userId ||
				scenario.shares.some((share) => share.userId === userId)
			);
		},
		[mode, userId],
	);

	return { isMyScenario };
}
